import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  answerParentalConsent,
  getParentalConsent,
  type ParentalConsentRequest,
} from '@/services/privacy.service'

type Phase = 'loading' | 'ask' | 'granted' | 'refused' | 'invalid'

/** A parent approves or refuses the account their child created (COPPA). */
export function ParentalConsentPage() {
  const { t } = useTranslation()
  const { token = '' } = useParams()
  const [phase, setPhase] = useState<Phase>('loading')
  const [request, setRequest] = useState<ParentalConsentRequest | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmRefuse, setConfirmRefuse] = useState(false)

  useEffect(() => {
    getParentalConsent(token)
      .then((data) => {
        setRequest(data)
        setPhase('ask')
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail
        setReason(typeof detail === 'string' ? detail : 'consent_link_invalid')
        setPhase('invalid')
      })
  }, [token])

  const answer = async (decision: 'grant' | 'refuse') => {
    setBusy(true)
    try {
      const { status } = await answerParentalConsent(token, decision)
      setPhase(status)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      setReason(typeof detail === 'string' ? detail : 'consent_link_invalid')
      setPhase('invalid')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-2">
          <ShieldCheck className="w-12 h-12 mx-auto text-primary-500" />
          <CardTitle className="text-2xl">{t('parentalConsent.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {phase === 'loading' && (
            <div className="flex justify-center py-6" role="status">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          )}

          {phase === 'ask' && request && (
            <>
              <p className="text-gray-700 dark:text-gray-300">
                {t('parentalConsent.intro', {
                  name: request.child_name,
                  email: request.child_email,
                  age: request.stated_age,
                })}
              </p>
              <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>{t('parentalConsent.whatWeKeep')}</li>
                <li>{t('parentalConsent.purpose')}</li>
                <li>{t('parentalConsent.yourRights', { contact: request.privacy_contact })}</li>
              </ul>
              <p className="text-sm">
                <a
                  href="https://myappacademia.com/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 underline"
                >
                  {t('parentalConsent.readPolicy')}
                </a>
              </p>
              {confirmRefuse ? (
                <Alert variant="destructive">
                  <AlertDescription className="space-y-3">
                    <p>{t('parentalConsent.refuseWarning')}</p>
                    <div className="flex gap-2">
                      <Button variant="destructive" disabled={busy} onClick={() => answer('refuse')}>
                        {t('parentalConsent.refuseConfirm')}
                      </Button>
                      <Button variant="outline" disabled={busy} onClick={() => setConfirmRefuse(false)}>
                        {t('parentalConsent.back')}
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    className="flex-1 bg-gradient-to-r from-primary-500 to-secondary-500"
                    disabled={busy}
                    onClick={() => answer('grant')}
                  >
                    {t('parentalConsent.grant')}
                  </Button>
                  <Button variant="outline" className="flex-1" disabled={busy} onClick={() => setConfirmRefuse(true)}>
                    {t('parentalConsent.refuse')}
                  </Button>
                </div>
              )}
            </>
          )}

          {phase === 'granted' && (
            <div className="text-center space-y-2" role="status">
              <CheckCircle2 className="w-10 h-10 mx-auto text-green-500" />
              <p>{t('parentalConsent.granted')}</p>
            </div>
          )}

          {phase === 'refused' && (
            <div className="text-center space-y-2" role="status">
              <XCircle className="w-10 h-10 mx-auto text-gray-500" />
              <p>{t('parentalConsent.refused')}</p>
            </div>
          )}

          {phase === 'invalid' && (
            <Alert>
              <AlertDescription>
                {t(`parentalConsent.errors.${reason}`, t('parentalConsent.errors.consent_link_invalid'))}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default ParentalConsentPage
